import { useEffect, useState } from "react";
import { adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial, listTestimonials, formatApiErrorDetail } from "@/lib/adminApi";
import Stars from "@/components/Stars";

const EMPTY = { name: "", quote: "", rating: 5 };

export default function TestimonialsPanel() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    try { setRows(await listTestimonials()); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);

  const reset = () => { setForm(EMPTY); setEditId(null); };

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const body = { name: form.name, quote: form.quote, rating: Number(form.rating) };
      if (editId) await adminUpdateTestimonial(editId, body);
      else await adminCreateTestimonial(body);
      reset();
      await load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const edit = (t) => { setEditId(t.id); setForm({ name: t.name, quote: t.quote, rating: t.rating }); };

  const remove = async (t) => {
    if (!window.confirm(`Delete the review from ${t.name}?`)) return;
    try { await adminDeleteTestimonial(t.id); if (editId === t.id) reset(); await load(); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <section className="admin-subs" data-testid="testimonials-panel">
      <p className="mono-caps admin-hint" data-testid="testimonials-count">{rows.length} review{rows.length === 1 ? "" : "s"} · shown in the closing section of the gallery</p>
      {error && <p className="admin-error" data-testid="testimonials-error">{error}</p>}

      <form className="admin-testi-form" onSubmit={save} data-testid="testimonial-form">
        <div className="admin-row">
          <label className="admin-field"><span className="mono-caps">Name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Claire M., Toronto" required data-testid="testimonial-field-name" />
          </label>
          <label className="admin-field"><span className="mono-caps">Rating</span>
            <select value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="testimonial-field-rating">
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} star{n === 1 ? "" : "s"}</option>)}
            </select>
          </label>
        </div>
        <label className="admin-field"><span className="mono-caps">Review</span>
          <textarea rows={4} value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} maxLength={800} required data-testid="testimonial-field-quote" />
        </label>
        <div className="admin-testi-form__actions">
          <button type="submit" className="admin-btn mono-caps" disabled={busy} data-testid="testimonial-save">
            {busy ? "Saving…" : editId ? "Save changes" : "Add review"}
          </button>
          {editId && <button type="button" className="admin-mini mono-caps" onClick={reset} data-testid="testimonial-cancel">Cancel</button>}
        </div>
      </form>

      {rows.length === 0 ? (
        <p className="admin-subs__empty" data-testid="testimonials-empty">No reviews yet.</p>
      ) : (
        <ul className="admin-inq">
          {rows.map((t) => (
            <li key={t.id} className="admin-inq__item" data-testid={`testimonial-row-${t.id}`}>
              <div className="admin-inq__head">
                <div>
                  <Stars rating={t.rating} size={13} className="admin-stars" />
                  <strong style={{ display: "block", marginTop: "0.3rem" }}>{t.name}</strong>
                </div>
                <div className="admin-card__actions">
                  <button type="button" className="admin-mini mono-caps" onClick={() => edit(t)} data-testid={`testimonial-edit-${t.id}`}>Edit</button>
                  <button type="button" className="admin-mini admin-mini--danger mono-caps" onClick={() => remove(t)} data-testid={`testimonial-delete-${t.id}`}>Delete</button>
                </div>
              </div>
              <p className="admin-inq__note">“{t.quote}”</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
